
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { updateDeliveryInfo } from "../../redux/actions/orderActions";

const OrderModal = ({ order, onClose }) => {
  const dispatch = useDispatch();
  const [loadingImage, setLoadingImage] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [deliveryMethod, setDeliveryMethod] = useState(order.deliveryMethod || "standard");
  const [deliveryStatus, setDeliveryStatus] = useState(order.deliveryStatus || "Ordered");
  const [estimatedDeliveryDate, setEstimatedDeliveryDate] = useState(
    order.estimatedDeliveryDate ? new Date(order.estimatedDeliveryDate).toISOString().split("T")[0] : ""
  );
  const [courierTrackingNumber, setCourierTrackingNumber] = useState(order.courierTrackingNumber || "");
  const [courierName, setCourierName] = useState(order.courierName || "");
  const [deliveryDate, setDeliveryDate] = useState(
    order.deliveryDate ? new Date(order.deliveryDate).toISOString().split("T")[0] : ""
  );

  const handleOpenImageInNewTab = (url) => {
    window.open(url, '_blank');
    setLoadingImage(false);
  };

  const handleUpdateDelivery = async () => {
    const payload = {};
    if (deliveryMethod) payload.deliveryMethod = deliveryMethod;
    if (deliveryStatus) payload.deliveryStatus = deliveryStatus;
    if (estimatedDeliveryDate) payload.estimatedDeliveryDate = estimatedDeliveryDate;
    if (courierTrackingNumber) payload.courierTrackingNumber = courierTrackingNumber;
    if (courierName) payload.courierName = courierName;
    if (deliveryStatus === "Delivered" && deliveryDate) payload.deliveredDate = deliveryDate;

    if (Object.keys(payload).length === 0) return;

    setUpdating(true);
    try {
      await dispatch(updateDeliveryInfo(order.orderId, payload));
    } catch (err) {
      // error handled in action
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center border-b p-4">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800">Order Details</h2>
          <button
            onClick={onClose}
            className="text-red-500 hover:text-red-700 font-bold text-xl"
          >
            ✕
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-gray-700">
                <strong className="text-gray-900">Order ID:</strong> {order.orderId}
              </p>
              <p className="text-sm text-gray-700">
                <strong className="text-gray-900">Customer Email:</strong> {order.customerEmail}
              </p>
              <p className="text-sm text-gray-700">
                <strong className="text-gray-900">Total Amount:</strong> ₹{order.totalAmount}
              </p>
              <p className="text-sm text-gray-700">
                <strong className="text-gray-900">Payment Status:</strong>{" "}
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${order.paymentStatus === 'pending' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                  {order.paymentStatus}
                </span>
              </p>
            </div>

            {/* Courier & Delivery Update Section */}
            <div className="space-y-3">
              <h3 className="text-md sm:text-lg font-semibold text-gray-800 border-b pb-2">Delivery Information</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Courier Name</label>
                  <input
                    type="text"
                    value={courierName}
                    onChange={(e) => setCourierName(e.target.value)}
                    placeholder="Enter courier name"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Courier Tracking Number</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={courierTrackingNumber}
                      onChange={(e) => setCourierTrackingNumber(e.target.value)}
                      placeholder="Enter tracking number"
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {courierTrackingNumber && (
                      <a
                        href={`https://trackcourier.io/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 bg-green-500 text-white text-sm rounded-md hover:bg-green-600 flex-shrink-0"
                      >
                        Track
                      </a>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Method</label>
                  <select
                    value={deliveryMethod}
                    onChange={(e) => setDeliveryMethod(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="standard">Standard</option>
                    <option value="express">Express</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Status</label>
                  <select
                    value={deliveryStatus}
                    onChange={(e) => setDeliveryStatus(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Ordered">Ordered</option>
                    <option value="Packed">Packed</option>
                    <option value="Shipped">Shipped</option>
                    <option value="Delivered">Delivered</option>
                    <optgroup label="Return">
                      <option value="Return Initiated">Return Initiated</option>
                      <option value="Return Accepted">Return Accepted</option>
                      <option value="Return Picked">Return Picked</option>
                      <option value="Return Completed">Return Completed</option>
                    </optgroup>
                    <optgroup label="Replacement">
                      <option value="Replacement Initiated">Replacement Initiated</option>
                      <option value="Replacement Accepted">Replacement Accepted</option>
                      <option value="Replacement Shipped">Replacement Shipped</option>
                      <option value="Replacement Completed">Replacement Completed</option>
                    </optgroup>
                  </select>
                </div>
                {deliveryStatus === "Delivered" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Date</label>
                    <input
                      type="date"
                      value={deliveryDate}
                      onChange={(e) => setDeliveryDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Delivery Date</label>
                  <input
                    type="date"
                    value={estimatedDeliveryDate}
                    onChange={(e) => setEstimatedDeliveryDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handleUpdateDelivery}
                    disabled={updating}
                    className="w-full bg-black text-white px-4 py-2 rounded-md text-sm hover:bg-gray-800 disabled:opacity-50"
                  >
                    {updating ? "Updating..." : "Update Delivery Info"}
                  </button>
                </div>
              </div>
            </div>

            {order.address && (
              <div className="space-y-2">
                <h3 className="text-md sm:text-lg font-semibold text-gray-800 border-b pb-2">Address</h3>
                <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-700 space-y-1">
                  <p><strong className="text-gray-900">Address Line 1:</strong> {order.address.addressLine1 || 'N/A'}</p>
                  <p><strong className="text-gray-900">Address Line 2:</strong> {order.address.addressLine2 || 'N/A'}</p>
                  <p><strong className="text-gray-900">City:</strong> {order.address.city || 'N/A'}</p>
                  <p><strong className="text-gray-900">State:</strong> {order.address.state || 'N/A'}</p>
                  <p><strong className="text-gray-900">Pin Code:</strong> {order.address.pinCode || 'N/A'}</p>
                  <p><strong className="text-gray-900">Country:</strong> {order.address.country || 'N/A'}</p>
                  <p><strong className="text-gray-900">Type:</strong>{" "}
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {order.address.type || 'N/A'}
                    </span>
                  </p>
                </div>
              </div>
            )}

            <div>
              <h3 className="text-md sm:text-lg font-semibold text-gray-800 border-b pb-2">Products</h3>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {order.products.map((product, index) => (
                  <div
                    key={index}
                    className="flex flex-col bg-gray-100 p-3 rounded-lg shadow-sm"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <img
                        src={product.primaryImageUrl}
                        alt={product.productName}
                        className="w-12 h-12 sm:w-16 sm:h-16 rounded-md object-cover flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{product.productName}</p>
                        <p className="text-xs text-gray-500 truncate">
                          <strong>Category:</strong> {product.categoryName}
                        </p>
                      </div>
                    </div>

                    {product.customization && (
                      <div className="mt-2 text-xs sm:text-sm text-gray-700 bg-white p-2 rounded-md">
                        <h4 className="font-semibold text-gray-900 mb-1">Customization:</h4>
                        <div className="space-y-1">
                          <p><strong>Size:</strong> {product.customization.tShirtSize || "N/A"}</p>
                          <p><strong>Quantity:</strong> {product.customization.quantity || "N/A"}</p>

                          <p className="flex items-center gap-1">
                            <strong>Color:</strong> 
                            <span 
                              className="w-3 h-3 rounded-full inline-block"
                              style={{ backgroundColor: product.customization.fontColor }}
                            />
                          </p>
                          <p><strong>Placement:</strong> {product.customization.placement || "N/A"}</p>

                          {product.customization.imagePrinturl && (
                            <div className="mt-2">
                              <p><strong>Image Print:</strong></p>
                              <div className="flex items-center gap-2 mt-1">
                                <img 
                                  src={product.customization.imagePrinturl} 
                                  alt="Custom Print"
                                  className="w-12 h-12 object-cover rounded-md border"
                                />
                                <button
                                  onClick={() => handleOpenImageInNewTab(product.customization.imagePrinturl)}
                                  className="text-xs bg-blue-500 text-white px-2 py-1 rounded-md hover:bg-blue-600"
                                >
                                  {loadingImage ? 'Opening...' : 'View Full'}
                                </button>
                              </div>
                            </div>
                          )}

                          {product.customization.textPrint && (
                            <p className="truncate"><strong>Text:</strong> {product.customization.textPrint}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t p-4">
          <button
            onClick={onClose}
            className="w-full bg-blue-500 text-white px-4 py-2 rounded-md shadow hover:bg-blue-600 text-sm sm:text-base"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderModal;
